import type { TFunction } from '@mukil/i18n';
import { FormField, MoneyInput, StyledSelect } from '@jumpstone/ui-react';
import type { PayCurrencyOption } from '../../data/use_pay_currencies';

// `.nv-input` pins `width: 100%`, so the picker's width comes from its
// wrapper (`w-28 shrink-0` below), not from a width utility on the trigger.
const CURRENCY_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2 disabled:cursor-not-allowed disabled:opacity-50';

export interface EmployeePayAmountFieldProps {
  id: string;
  /** Translated label (the caller resolves it, so the i18n extractor sees a literal key). */
  label: string;
  error: string | undefined;
  amountCents: number;
  onAmountChange: (cents: number) => void;
  /** The draft's pay currency -- `''` means the organisation base currency. */
  payCurrencyCode: string;
  onPayCurrencyChange: (code: string) => void;
  baseCurrency: string;
  payCurrencies: readonly PayCurrencyOption[];
  t: TFunction;
}

/**
 * Salary / hourly-rate input with its pay-currency picker beside it. Picking
 * the base currency writes `''` (stored as NULL = "follow the base"), so an
 * untouched legacy row is never rewritten to an explicit base code. A
 * non-base choice shows a hint: pay runs leave non-base employees out until
 * multi-currency payroll exists (`payroll_pay_currency.ts`).
 */
export function EmployeePayAmountField({
  id,
  label,
  error,
  amountCents,
  onAmountChange,
  payCurrencyCode,
  onPayCurrencyChange,
  baseCurrency,
  payCurrencies,
  t,
}: EmployeePayAmountFieldProps): React.JSX.Element {
  const selected = payCurrencyCode === '' ? baseCurrency : payCurrencyCode;
  return (
    <FormField
      label={label}
      htmlFor={id}
      error={error}
      helpText={
        selected !== baseCurrency
          ? t('employees.form_pay_currency_foreign_hint', {
              base: baseCurrency,
            })
          : undefined
      }
    >
      <div className="flex items-center gap-2">
        <div className="w-28 shrink-0">
          <StyledSelect
            value={selected}
            onChange={(code) => {
              onPayCurrencyChange(code === baseCurrency ? '' : code);
            }}
            options={payCurrencies.map((c) => ({
              value: c.code,
              label: c.code,
            }))}
            ariaLabel={t('employees.form_pay_currency')}
            triggerClassName={CURRENCY_TRIGGER_CLASSES}
            disabled={payCurrencies.length <= 1}
          />
        </div>
        <div className="min-w-0 flex-1">
          <MoneyInput id={id} value={amountCents} onChange={onAmountChange} />
        </div>
      </div>
    </FormField>
  );
}
